import type { SearchMatch } from '@vivport/backend/types';

type LocatorRecord = Record<string, unknown>;

function asLocatorRecord(locator: SearchMatch['locator']): LocatorRecord | null {
  if (locator === null || typeof locator !== 'object' || Array.isArray(locator)) {
    return null;
  }
  return locator as LocatorRecord;
}

export function describeSearchLocatorChip(match: SearchMatch): string | null {
  const record = asLocatorRecord(match.locator);
  switch (match.segmentKind) {
    case 'PAGE':
      return typeof record?.page === 'number' ? `p. ${record.page}` : 'page';
    case 'PASSAGE':
      return 'passage';
    case 'BODY':
      return 'section';
    case 'PARTY': {
      const role = typeof record?.role === 'string' ? record.role : null;
      return role === 'SENDER' || role === 'ORGANIZER' ? 'from' : 'to';
    }
    case 'IDENTIFIER':
      return 'id';
    case 'LOCATION':
      return 'location';
    default:
      return null;
  }
}

export interface SnippetPart {
  text: string;
  marked: boolean;
}

export function splitSnippetMarks(snippet: string): SnippetPart[] {
  const parts: SnippetPart[] = [];
  const pattern = /\[\[(.*?)\]\]/g;
  let lastIndex = 0;
  for (const match of snippet.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      parts.push({ text: snippet.slice(lastIndex, index), marked: false });
    }
    if (match[1].length > 0) {
      parts.push({ text: match[1], marked: true });
    }
    lastIndex = index + match[0].length;
  }
  if (lastIndex < snippet.length) {
    parts.push({ text: snippet.slice(lastIndex), marked: false });
  }
  return parts;
}

export function stripSnippetMarks(snippet: string): string {
  return snippet.replace(/\[\[|\]\]/g, '');
}

export function snippetQuote(snippet: string): string {
  return stripSnippetMarks(snippet).replace(/^…|…$/g, '').trim();
}
